<script setup lang="ts">
import {computed} from "vue";
import dayjs from "dayjs";
import {Cake, ScanLine} from "@lucide/vue";
import {CustomerBirthdayVoucher} from "@/birthday-gift/interfaces";

/**
 * Counter reminder: this patient still has a birthday gift to collect.
 *
 * Shared by the patient file and the detection pop-up, so both read the same
 * wording. The gift itself is never named — the team discovers it by scanning.
 */
const props = defineProps<{
    voucher: CustomerBirthdayVoucher;
    /** The pop-up is cramped: it uses a denser layout. */
    compact?: boolean;
}>();

const emit = defineEmits<{
    (e: 'scan'): void;
}>();

const daysSinceSent = computed(() => props.voucher.sent_at
    ? Math.max(0, dayjs().startOf('day').diff(dayjs(props.voucher.sent_at).startOf('day'), 'day'))
    : null);

const daysLeft = computed(() => props.voucher.expires_at
    ? Math.max(0, dayjs(props.voucher.expires_at).startOf('day').diff(dayjs().startOf('day'), 'day'))
    : null);

const sentLabel = computed(() => {
    if (daysSinceSent.value === null) {
        return 'Anniversaire envoyé';
    }

    return daysSinceSent.value === 0
        ? 'Anniversaire envoyé aujourd\'hui'
        : `Anniversaire envoyé il y a ${daysSinceSent.value} j`;
});

const expiryLabel = computed(() => {
    if (daysLeft.value === null) {
        return null;
    }

    return daysLeft.value === 0
        ? 'Dernier jour pour retirer le cadeau'
        : `Cadeau valable encore ${daysLeft.value} j`;
});
</script>

<template>
  <div
    class="flex items-center gap-2 rounded-lg border border-solid border-apodisPink bg-apodisPink/30"
    :class="compact ? 'px-2.5 py-2' : 'px-4 py-3'"
  >
    <Cake
      class="shrink-0 text-primary"
      :class="compact ? 'h-4 w-4' : 'h-5 w-5'"
    />
    <div class="min-w-0 flex-1">
      <div
        class="font-semibold leading-tight"
        :class="compact ? 'text-xs' : 'text-sm'"
      >
        {{ sentLabel }}
      </div>
      <div
        v-if="expiryLabel"
        class="leading-tight text-muted-foreground"
        :class="compact ? 'text-[11px]' : 'text-xs'"
      >
        {{ expiryLabel }}
      </div>
    </div>
    <button
      type="button"
      class="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-solid border-primary bg-white font-semibold text-primary transition-colors hover:bg-primary-lightest"
      :class="compact ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'"
      @click="emit('scan')"
    >
      <ScanLine :class="compact ? 'h-3.5 w-3.5' : 'h-4 w-4'" />Scanner
    </button>
  </div>
</template>
